import { useEffect } from 'react';
import type { QuizType } from '../../store/clientQuiz/api';
import type { LinkState } from './publicQuizLink';
import { QUIZ_LANGS } from './quizLang';
import type { QuizLang } from './quizLang';

/**
 * An invitation's answers in progress, kept on the device so the client can
 * pick up where they left off. On an iPhone the quiz is usually interrupted by
 * WhatsApp, and the client comes back one of three ways:
 *
 * - the app switcher, after iOS discarded and reloaded the Safari tab;
 * - tapping the same link in the chat again, which opens a NEW Safari tab;
 * - after iOS terminated Safari itself, which restores the tab's URL only.
 *
 * sessionStorage belongs to one tab and dies with Safari, so it covers only the
 * first. localStorage covers all three, keyed by the invitation's token.
 *
 * Only a real `?t=` link has a key: a guest preview saves nothing (spec §5.2).
 * The draft never leaves the device, is deleted once the answers are sent or
 * the link turns out to be dead, and expires on its own after
 * QUIZ_DRAFT_MAX_AGE_MS so a shared phone does not keep half-answered quizzes.
 *
 * Its own module, importing types (and the pure language list) only:
 * CareerQuizPartTwo reads it too, and publicQuizLink's api import would drag
 * the fetch wrapper (and the Redux store) into that component's tests.
 */

const PREFIX = 'quizDraft.v2.';
export const QUIZ_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

type Envelope = { at: number; data: object };

export const quizDraftKey = (type: QuizType, token: string | null): string | null =>
  token ? `${PREFIX}${type}.${token}` : null;

const parseEnvelope = (raw: string | null): Envelope | null => {
  try {
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed &&
      typeof parsed === 'object' &&
      typeof parsed.at === 'number' &&
      parsed.data &&
      typeof parsed.data === 'object' &&
      !Array.isArray(parsed.data)
      ? parsed
      : null;
  } catch (_) {
    return null;
  }
};

const isFresh = (envelope: Envelope, now: number) => now - envelope.at < QUIZ_DRAFT_MAX_AGE_MS;

/** Expired (or unreadable) drafts of any invitation go, so storage does not collect them. */
const pruneQuizDrafts = (now: number) => {
  const stale: string[] = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (!key || !key.startsWith(PREFIX)) continue;
    const envelope = parseEnvelope(window.localStorage.getItem(key));
    if (!envelope || !isFresh(envelope, now)) stale.push(key);
  }
  stale.forEach(key => window.localStorage.removeItem(key));
};

export const readQuizDraft = <T extends object>(key: string | null): Partial<T> | null => {
  if (!key) return null;
  try {
    const now = Date.now();
    pruneQuizDrafts(now);
    const envelope = parseEnvelope(window.localStorage.getItem(key));
    return envelope && isFresh(envelope, now) ? (envelope.data as Partial<T>) : null;
  } catch (_) {
    return null;
  }
};

/** A language saved in a draft, if it is still one of the page's three. */
export const draftLang = (value: unknown): QuizLang | null =>
  typeof value === 'string' && (QUIZ_LANGS as string[]).includes(value) ? (value as QuizLang) : null;

/** A link that will never take these answers: the draft has nothing left to wait for. `offline` / `busy` keep it. */
export const linkHasEnded = (state: LinkState): boolean =>
  state === 'invalid' || state === 'expired' || state === 'done' || state === 'mismatch';

/**
 * Writes `snapshot` while `save`; removes the draft once `clear` (which wins).
 * Call it above any early return (rules of hooks).
 */
export const useQuizDraft = (key: string | null, snapshot: object, options: { save: boolean; clear: boolean }) => {
  const json = key && options.save && !options.clear ? JSON.stringify(snapshot) : null;
  const clear = Boolean(key) && options.clear;
  useEffect(() => {
    if (!key) return;
    try {
      if (clear) window.localStorage.removeItem(key);
      else if (json) window.localStorage.setItem(key, `{"at":${Date.now()},"data":${json}}`);
    } catch (_) {
      // Storage blocked or full: the quiz still works, coming back just starts over.
    }
  }, [key, json, clear]);
};
